import AddIcon from "@mui/icons-material/Add";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { IconButton, Switch, TextField, Tooltip } from "@mui/material";
import React, { Suspense, useMemo } from "react";
import toast from "react-hot-toast";

import { DatabaseCollectionDto } from "../lib/generated/api-client";
import { prettyJson } from "../lib/format";
import {
  Field,
  InputKind,
  enumOptions,
  fieldLabel,
  humanize,
  inputKindFor,
  isNativeDate,
  isNativeNumber,
  isReadOnlyField,
  isReadOnlyOnCreate,
  isRequired,
  isSecretField,
  looksLikeUrl,
  singularize,
} from "../lib/schema-field";
import { JSONEditor } from "./JSONEditor";
import { AutocompleteSelect } from "./AutocompleteSelect";
import { Button } from "./ui/Button";
import { PasswordInput } from "./ui/PasswordInput";

/**
 * Lazy on purpose. EntityPicker renders a nested create-record form, so a static
 * import closes the cycle DynamicFormField -> EntityPicker -> useDynamicForm ->
 * DynamicForm -> DynamicFormField, and ESM resolves that as "cannot access
 * before initialization" at runtime.
 */
const EntityPicker = React.lazy(() =>
  import("./EntityPicker").then((m) => ({ default: m.EntityPicker }))
);

export type FieldError = string | undefined;

type DynamicFormFieldProps = {
  field: Field;
  collection?: DatabaseCollectionDto;
  model: Record<string, any>;
  onChangeForm: (name: string, val: any) => void;
  debugMode?: boolean;
  error?: FieldError;
  demoMode?: boolean;
  /** When false, NOT NULL no-default audit columns are editable. */
  isUpdate?: boolean;
};

/* ------------------------------------------------------- value conversions */

/**
 * `new Date(v).toISOString().slice(0, 10)` shifts the day backwards for anyone
 * west of UTC, so a stored 2024-01-01 renders as 2023-12-31 and saves wrong.
 * These helpers stay in local time.
 */
const pad = (n: number) => String(n).padStart(2, "0");

export const toDateInput = (value: unknown): string => {
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}T/.test(value)) {
    return value.slice(0, 10);
  }
  if (!value) return "";
  const d = new Date(value as any);
  if (isNaN(d.getTime())) return "";
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

export const toDateTimeInput = (value: unknown): string => {
  if (!value) return "";
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(value)) {
    return value.slice(0, 16);
  }
  const d = new Date(value as any);
  if (isNaN(d.getTime())) return "";
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
    d.getHours()
  )}:${pad(d.getMinutes())}`;
};

export const toTimeInput = (value: unknown): string => {
  if (typeof value === "string") {
    const match = value.match(/(\d{2}):(\d{2})/);
    if (match) return `${match[1]}:${match[2]}`;
  }
  if (!value) return "";
  const d = new Date(value as any);
  if (isNaN(d.getTime())) return "";
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

/** Sampled `max` ≤ 1 (or unset, same default as the old fraction hint) means 0–1 storage. */
export const isFractionPercent = (field: Field): boolean =>
  (field.max ?? 1) <= 1;

/**
 * Show 0–1 fractions as 0–100 in the editor. Values already > 1 stay as-is
 * (column stores whole percents). Empty stays empty; 0 stays 0.
 */
export const toPercentInput = (value: unknown, field: Field): string => {
  if (value == null || value === "") return "";
  const n = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(n)) return String(value);
  if (isFractionPercent(field) && n >= 0 && n <= 1) {
    return String(Number((n * 100).toFixed(10)));
  }
  return String(n);
};

/** Inverse of `toPercentInput`. Native NUMBER columns persist n/100; others keep the typed string. */
export const parsePercentInput = (
  raw: string,
  field: Field,
  native: boolean
): number | string | null => {
  if (raw === "") return null;
  if (!native) return raw;
  const parsed = Number(raw);
  if (Number.isNaN(parsed)) return raw;
  return isFractionPercent(field) ? parsed / 100 : parsed;
};

/** Relation labels and search controls need the same room as prose / JSON. */
export const fieldSpan = (field: Field, hasRelation = false): 1 | 2 => {
  const kind = inputKindFor(field);
  return hasRelation || kind === "json" || kind === "longtext" ? 2 : 1;
};

/* ------------------------------------------------------------------ labels */

const Label: React.FC<{
  htmlFor: string;
  children: React.ReactNode;
  required?: boolean;
  locked?: boolean;
  hint?: string;
}> = ({ htmlFor, children, required, locked, hint }) => (
  <label
    id={`${htmlFor}-label`}
    htmlFor={htmlFor}
    className="mb-1.5 flex items-center gap-1.5 text-xs font-medium text-od-text"
  >
    <span className="truncate">{children}</span>
    {required && (
      <span className="text-od-danger" aria-hidden title="Required">
        *
      </span>
    )}
    {locked && (
      <Tooltip title={hint ?? "Managed by the database"}>
        <LockOutlinedIcon sx={{ fontSize: 12, color: "var(--od-muted)" }} />
      </Tooltip>
    )}
  </label>
);

const HelpText: React.FC<{ error?: string; hint?: string }> = ({
  error,
  hint,
}) => {
  if (!error && !hint) return null;
  return (
    <p
      className="m-0 mt-1 text-xs leading-4"
      style={{ color: error ? "var(--od-danger)" : "var(--od-muted)" }}
    >
      {error ?? hint}
    </p>
  );
};

const inputSx = {
  width: "100%",
};

/** Relation metadata may contain either a target collection name (`owners`) or
 * a database constraint name (`projects_owner_id_fkey`). Only the former is a
 * useful user-facing label. */
const relationLabel = (field: Field, relationName?: string): string => {
  const candidate = relationName?.trim() ?? "";
  const relationWords = humanize(candidate)
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean);
  const isGenericOrConstraint =
    relationWords.length === 0 ||
    relationWords.some((word) =>
      ["fk", "fkey", "id", "ids", "pkey"].includes(word)
    ) ||
    relationWords.join(" ") === "foreign key";

  if (!isGenericOrConstraint) {
    return singularize(candidate);
  }

  const rawFieldLabel = field.label || field.name;
  const withoutTrailingId = rawFieldLabel
    .replace(/(?:[\s_-]+id|Id)$/i, "")
    .trim();
  return humanize(withoutTrailingId || rawFieldLabel);
};

/* ------------------------------------------------------------------- field */

export const DynamicFormField: React.FC<DynamicFormFieldProps> = ({
  field,
  collection,
  model,
  onChangeForm,
  debugMode = false,
  error,
  demoMode = false,
  isUpdate,
}) => {
  const { name, isArray, children } = field;
  const value = model?.[name];
  const label = fieldLabel(field);
  const instanceId = React.useId();
  const inputId = `field-${instanceId}`;

  const relation = collection?.relations?.find(
    (r) =>
      r.databaseFieldId === collection?.fields?.find((f) => f.name === name)?.id
  );
  const displayLabel = relation ? relationLabel(field, relation.name) : label;

  const handleNestedChange = (childName: string, childValue: any) => {
    onChangeForm(name, { ...(value || {}), [childName]: childValue });
  };

  if (children?.length && !isArray) {
    return (
      <fieldset className="col-span-full m-0 rounded-lg border border-od-border bg-od-surface-2/50 p-3">
        <legend className="px-1 text-xs font-semibold text-od-text">
          {label}
        </legend>
        <div className="grid grid-cols-1 gap-x-4 gap-y-3 sm:grid-cols-2">
          {children.map((child, index) => (
            <DynamicFormField
              key={`${name}-${index}`}
              field={child as Field}
              collection={collection}
              model={model[name] || {}}
              onChangeForm={handleNestedChange}
              debugMode={debugMode}
              demoMode={demoMode}
              isUpdate={isUpdate}
            />
          ))}
        </div>
      </fieldset>
    );
  }

  if (isArray) {
    const items: any[] = Array.isArray(value) ? value : [];
    const addItem = () => {
      const template = children?.length
        ? children.reduce((acc, child) => {
            acc[child.name] = defaultValueFor(child as Field);
            return acc;
          }, {} as Record<string, any>)
        : defaultValueFor({ ...field, isArray: false });
      onChangeForm(name, [...items, template]);
    };

    return (
      <fieldset className="col-span-full m-0 rounded-lg border border-od-border bg-od-surface-2/50 p-3">
        <legend className="px-1 text-xs font-semibold text-od-text">
          {label}
          <span className="ml-1.5 font-normal text-od-muted">
            {items.length} {items.length === 1 ? "item" : "items"}
          </span>
        </legend>

        {items.length === 0 && (
          <p className="m-0 mb-2 text-xs text-od-muted">Nothing added yet.</p>
        )}

        <div className="space-y-2">
          {items.map((item, index) => (
            <div
              key={index}
              className="flex items-start gap-2 rounded-lg border border-od-border bg-od-surface p-2.5"
            >
              <span className="mt-1.5 w-6 shrink-0 text-xs font-medium text-od-muted">
                #{index + 1}
              </span>
              <div className="min-w-0 flex-1">
                <DynamicFormField
                  collection={collection}
                  field={{ ...field, isArray: false, children: field.children }}
                  model={{ [name]: item }}
                  onChangeForm={(_childName, newValue) => {
                    const updated = [...items];
                    updated[index] = newValue;
                    onChangeForm(name, updated);
                  }}
                  debugMode={debugMode}
                  demoMode={demoMode}
                  isUpdate={isUpdate}
                />
              </div>
              {!demoMode && (
              <Tooltip title="Remove item">
                  <IconButton
                    size="small"
                    aria-label={`Remove item ${index + 1}`}
                    onClick={() =>
                      onChangeForm(
                        name,
                        items.filter((_, i) => i !== index)
                      )
                    }
                    sx={{
                      color: "var(--od-muted)",
                      "&:hover": { color: "var(--od-danger)" },
                    }}
                  >
                    <DeleteOutlineIcon sx={{ fontSize: 17 }} />
                  </IconButton>
              </Tooltip>
              )}
            </div>
          ))}
        </div>

        {!demoMode && (
        <Button
          onClick={addItem}
          variant="secondary" size="sm" className="mt-2.5 self-start"
        >
          <AddIcon sx={{ fontSize: 14 }} /> Add item
        </Button>
        )}
      </fieldset>
    );
  }

  const locked =
    isUpdate === false ? isReadOnlyOnCreate(field) : isReadOnlyField(field);
  const span = fieldSpan(field, !!relation);

  return (
    <div className={span === 2 ? "sm:col-span-2" : undefined}>
      <Label
        htmlFor={inputId}
        required={isRequired(field) && !locked && !(isUpdate && isSecretField(field))}
        locked={locked}
      >
        {displayLabel}
      </Label>

      {relation ? (
        <Suspense
          fallback={
            <div className="min-h-11 sm:h-8 sm:min-h-0 animate-pulse rounded-md border border-od-border bg-od-surface-2" />
          }
        >
          <EntityPicker
            id={inputId}
            datasourceId={collection?.datasourceId!}
            collectionId={relation.targetCollectionId}
            value={value}
            onChange={(next) => onChangeForm(name, next)}
            label=""
            placeholder={`Search ${displayLabel}\u2026`}
            required={isRequired(field) && !locked}
            demoMode={demoMode}
          />
        </Suspense>
      ) : (
        <FieldInput
          id={inputId}
          field={field}
          value={value}
          locked={!!locked}
          invalid={!!error}
          isUpdate={isUpdate}
          onChange={(next) => onChangeForm(name, next)}
          demoMode={demoMode}
        />
      )}

      <HelpText error={error} />

      {debugMode && (
        <p className="m-0 mt-1 font-mono text-xs text-od-muted">
          {field.type} → {field.subType} · {typeof value}
        </p>
      )}
    </div>
  );
};

/* ------------------------------------------------------------------ inputs */

type FieldInputProps = {
  id: string;
  field: Field;
  value: any;
  locked: boolean;
  invalid: boolean;
  onChange: (value: any) => void;
  demoMode?: boolean;
  isUpdate?: boolean;
};

const FieldInput: React.FC<FieldInputProps> = ({
  id,
  field,
  value,
  locked,
  invalid,
  onChange,
  demoMode = false,
  isUpdate,
}) => {
  const kind = useMemo<InputKind>(
    () => isSecretField(field) ? "password" : inputKindFor(field),
    [field]
  );
  const options = useMemo(() => enumOptions(field), [field]);
  const common = {
    id,
    fullWidth: true,
    error: invalid,
    disabled: locked || demoMode,
    sx: inputSx,
  } as const;

  switch (kind) {
    case "boolean": {
      const on = value === true || value === "true" || value === 1;
      return (
        <label className="flex min-h-11 sm:h-8 sm:min-h-0 items-center gap-2 text-xs text-od-text">
          <Switch
            id={id}
            size="small"
            checked={on}
            disabled={locked || demoMode}
            onChange={(e) => onChange(e.target.checked)}
          />
          <span className={on ? "text-od-text" : "text-od-muted"}>
            {on ? "Yes" : "No"}
          </span>
        </label>
      );
    }

    case "enum":
      return (
        <AutocompleteSelect
          id={id}
          ariaLabel={fieldLabel(field)}
          placeholder={`Select ${fieldLabel(field)}`}
          options={(options ?? []).map((option) => ({ label: option, value: option }))}
          value={value ?? null}
          onChange={(next) => onChange(Array.isArray(next) ? next[0] ?? null : next)}
          disabled={locked || demoMode}
          disableClearable={field.nullable === false}
          freeSolo
          commitTypedOnBlur
        />
      );

    case "number":
    case "currency":
    case "percentage": {
      // Postgres returns `numeric` as a string, and the analyser sometimes tags
      // a plain varchar (a phone number, a zero-padded code) as INTEGER. A
      // native number input would blank both, so only a column the analyser
      // typed NUMBER outright gets one; everything else stays a text input and
      // round-trips the exact stored string.
      const native = isNativeNumber(field);
      const fraction = kind === "percentage" && isFractionPercent(field);
      const display =
        kind === "percentage" ? toPercentInput(value, field) : value ?? "";
      return (
        <TextField
          {...common}
          type={native ? "number" : "text"}
          value={display}
          inputProps={{
            step: native ? (field.subType === "INTEGER" ? 1 : "any") : undefined,
            min:
              native && fraction && field.min != null
                ? field.min * 100
                : native
                  ? field.min
                  : undefined,
            max:
              native && fraction && field.max != null
                ? field.max * 100
                : native
                  ? field.max
                  : undefined,
            inputMode: "decimal",
          }}
          InputProps={{
            startAdornment:
              kind === "currency" ? (
                <span className="mr-1 text-xs text-od-muted">$</span>
              ) : undefined,
            endAdornment:
              kind === "percentage" ? (
                <span className="ml-1 text-xs text-od-muted">%</span>
              ) : undefined,
          }}
          onChange={(e) => {
            const raw = e.target.value;
            if (kind === "percentage") {
              return onChange(parsePercentInput(raw, field, native));
            }
            if (raw === "") return onChange(null);
            if (!native) return onChange(raw);
            const parsed = Number(raw);
            onChange(isNaN(parsed) ? raw : parsed);
          }}
        />
      );
    }

    case "date":
    case "datetime":
    case "time": {
      const convert =
        kind === "date"
          ? toDateInput
          : kind === "datetime"
          ? toDateTimeInput
          : toTimeInput;
      const converted = convert(value);

      // Two ways a native picker would destroy data, both seen on real
      // schemas. First: the analyser tagged a STRING column with a date
      // subType, which is how a `numeric` budget column ends up as TIMESTAMP;
      // a picker there is empty on create and saves a date into a number.
      // Second: a genuine DATE column holds something the picker cannot parse.
      // Either way, fall back to a plain text input.
      const untrustedTemporal =
        kind !== "time" && !isNativeDate(field);
      const unparseable = value != null && value !== "" && converted === "";

      if (untrustedTemporal || unparseable) {
        return (
          <TextField
            {...common}
            value={value == null ? "" : String(value)}
            helperText={
              unparseable
                ? "Stored value is not a valid date, editing as text"
                : undefined
            }
            onChange={(e) => onChange(e.target.value)}
          />
        );
      }

      return (
        <TextField
          {...common}
          type={
            kind === "date"
              ? "date"
              : kind === "datetime"
              ? "datetime-local"
              : "time"
          }
          value={converted}
          onChange={(e) => onChange(e.target.value || null)}
        />
      );
    }

    case "json":
      return (
        <JsonField id={id} value={value} locked={locked} invalid={invalid} onChange={onChange} demoMode={demoMode} />
      );

    case "longtext":
      return (
        <TextField
          {...common}
          multiline
          minRows={3}
          maxRows={12}
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
        />
      );

    case "color": {
      const hex = normaliseHex(value);
      return (
        <div className="flex items-center gap-2">
          <input
            type="color"
            aria-label="Colour picker"
            value={hex || "#000000"}
            disabled={locked || demoMode}
            onChange={(e) => onChange(e.target.value)}
            className="h-11 sm:h-8 w-10 shrink-0 cursor-pointer rounded-md border border-od-border bg-od-surface p-1"
          />
          <TextField
            {...common}
            value={value ?? ""}
            placeholder="#4f46e5"
            onChange={(e) => onChange(e.target.value)}
          />
        </div>
      );
    }

    case "password":
      return (
        <PasswordInput
          id={id}
          disabled={locked || demoMode}
          aria-invalid={invalid || undefined}
          placeholder={isUpdate ? "Leave blank to keep" : undefined}
          autoComplete="new-password"
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
        />
      );

    default: {
      // email / url / phone / text all use a plain text input on purpose: the
      // analyser mislabels short strings often enough that a native `type=url`
      // or `type=number` would refuse perfectly valid stored values.
      const stringValue = value == null ? "" : String(value);
      const showOpen = kind === "url" && looksLikeUrl(stringValue);
      const showCopy = !showOpen && stringValue.length > 20;

      return (
        <TextField
          {...common}
          value={stringValue}
          inputMode={kind === "phone" ? "tel" : undefined}
          onChange={(e) => onChange(e.target.value)}
          InputProps={{
            endAdornment: showOpen ? (
              <Tooltip title="Open link">
                <IconButton
                  size="small"
                  component="a"
                  href={stringValue}
                  target="_blank"
                  rel="noreferrer noopener"
                  sx={{ color: "var(--od-muted)" }}
                >
                  <OpenInNewIcon sx={{ fontSize: 15 }} />
                </IconButton>
              </Tooltip>
            ) : showCopy ? (
              <Tooltip title="Copy value">
                <IconButton
                  size="small"
                  onClick={() => {
                    navigator.clipboard?.writeText(stringValue);
                    toast.success("Copied");
                  }}
                  sx={{ color: "var(--od-muted)" }}
                >
                  <ContentCopyIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </Tooltip>
            ) : undefined,
          }}
        />
      );
    }
  }
};

/* -------------------------------------------------------------- json field */

const JsonField: React.FC<{
  id: string;
  value: any;
  locked: boolean;
  invalid: boolean;
  onChange: (value: any) => void;
  demoMode?: boolean;
}> = ({ id, value, locked, invalid, onChange, demoMode = false }) => {
  const [raw, setRaw] = React.useState<string | null>(null);
  const [mode, setMode] = React.useState<"tree" | "text">("tree");

  const parsed = useMemo(() => {
    if (typeof value === "string") {
      try {
        return JSON.parse(value);
      } catch {
        return undefined;
      }
    }
    return value;
  }, [value]);

  const canTree = parsed !== undefined && parsed !== null && typeof parsed === "object";

  return (
    <div className="rounded-lg border border-od-border bg-od-surface">
      <div className="flex items-center justify-between border-b border-od-border px-2 py-1">
        <span className="text-xs font-medium uppercase tracking-wide text-od-muted">
          JSON
        </span>
        <div className="flex gap-1">
          {(["tree", "text"] as const).map((m) => (
            <Button
              key={m}
              disabled={m === "tree" && !canTree}
              onClick={() => {
                if (m === "text") setRaw(prettyJson(value));
                setMode(m);
              }}
              variant="ghost"
              size="sm"
              aria-pressed={mode === m}
              className={mode === m ? "bg-od-surface-2 text-od-text" : undefined}
            >
              {m === "tree" ? "Tree" : "Raw"}
            </Button>
          ))}
        </div>
      </div>

      {mode === "tree" && canTree ? (
        <div role="group" aria-labelledby={`${id}-label`} className="max-h-72 overflow-auto p-1">
          <JSONEditor value={parsed} onChange={demoMode ? () => {} : onChange} />
        </div>
      ) : (
        <textarea
          id={id}
          spellCheck={false}
          disabled={locked || demoMode}
          value={raw ?? prettyJson(value)}
          onChange={(e) => {
            setRaw(e.target.value);
            try {
              onChange(JSON.parse(e.target.value));
            } catch {
              // Keep the raw text so the user can finish typing; validation on
              // submit reports the parse failure.
              onChange(e.target.value);
            }
          }}
          className="block max-h-72 w-full resize-y bg-transparent p-2.5 font-mono text-[12px] leading-5 text-od-text outline-none focus-visible:ring-2 focus-visible:ring-od-primary"
          rows={6}
          style={invalid ? { color: "var(--od-danger)" } : undefined}
        />
      )}
    </div>
  );
};

/* ----------------------------------------------------------------- helpers */

const normaliseHex = (value: unknown): string => {
  const str = String(value ?? "").trim();
  if (/^#[0-9a-f]{6}$/i.test(str)) return str;
  if (/^[0-9a-f]{6}$/i.test(str)) return `#${str}`;
  return "";
};

export const defaultValueFor = (field: Field): any => {
  switch (inputKindFor(field)) {
    case "boolean":
      return false;
    case "number":
    case "currency":
    case "percentage":
      return null;
    case "date":
    case "datetime":
    case "time":
      return null;
    case "json":
      return {};
    default:
      return "";
  }
};
